import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { ReactNode, useEffect, useState } from "react";
import * as yup from "yup";
import { useAppSelector } from "../../../../app/hooks";
import DisplayMoney from "../../../../common/custom/DisplayMoney";
import GuardianDropdown from "../../../../common/dropdowns/Guardian";
import CircularLoader from "../../../../common/loaders/circularLoader";
import { selectApplicant } from "../../../applicant/ApplicantSlice";
import {
  AllApplicantDto,
  ApplicantViewDto,
  LegalGuardianDetailDto
} from "../../../applicant/Models";
import { selectAdmin } from "../../adminSlice";
import { ApplicantDocument, MeansTestDto } from "../../models";
import GuardianSalaryCalculation from "../GuardianSalaryCalculations";
import { Zar } from "../../../../common/Logic/commonLogic";

interface Props {
  formik: FormikProps<any>;
  application: AllApplicantDto;
  fullApplicationDetails: ApplicantViewDto | undefined
}

export default function AssessmentMeansTest({ formik, application , fullApplicationDetails}: Props) {
  const selectAdminData = useAppSelector(selectAdmin);
  const [data, setData] = useState<ApplicantDocument>();
  const [showResults, setShowResults] = useState<boolean>(false);
  const selectApplicantData = useAppSelector(selectApplicant);
  const [isEnabled, setIsEnabled] = useState(false);

  const mapEnums = (key: number) => {
    switch (key) {
      case 0:
        return "Employed";
      case 1:
        return "SelfEmployed";
      case 2:
        return "Unemployed";
      case 3:
        return "Pensioner";
      default:
        return -1;
    }
  };
  const CustomInputField = (label: string, props: any) => {
    return (
      <TextField
        {...props}
        fullWidth
        size="small"
        label={label}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        type="number"
      />
    );
  };

 

  const calculateTotalMonthlyIncome = () => {
    if (!formik.values.meansTests || formik.values.meansTests.length === 0) {
      return 0;
    }

    let sum = 0;
    formik.values.meansTests.forEach((means: MeansTestDto) => {
      const month1 = Number(means.monthOne) || 0;
      const month2 = Number(means.monthTwo) || 0;
      const month3 = Number(means.monthThree) || 0;
      sum += (month1 + month2 + month3) / 3;
    });
    return Math.round(sum);
  };

  const calculateTotalYearlyIncome = () => {
    return Math.round(calculateTotalMonthlyIncome() * 12);
  };

  const calculateTotalDependantMonthlyIncome = () => {
    const dependants = Math.max(1, application.numberOfDependants || 1);
    return Math.round(calculateTotalMonthlyIncome() / dependants);
  };

  const calculateTotalDependantYearlyIncome = () => {
    const dependants = Math.max(1, application.numberOfDependants || 1);
    return Math.round(
      (calculateTotalMonthlyIncome() * 12) / dependants
    );
  };

  useEffect(() => {
    const applicatantDocuments =
      selectAdminData.applicantDocumentState.applicantDocuments;
    if (applicatantDocuments) {
      setData(applicatantDocuments);

      let meansTests = [] as MeansTestDto[];
      applicatantDocuments.guardians.forEach(
        (guardian: LegalGuardianDetailDto) => {
          const meansTest = {
            fullName: `${guardian.firstName} ${guardian.surname}`,
            identityNumber: guardian.identityNumber,
            monthOne: 0,
            monthTwo: 0,
            monthThree: 0,
            employementStatus: mapEnums(guardian.employmentStatus),
          } as MeansTestDto;

          meansTests.push(meansTest);
        }
      );
      formik.setFieldValue("meansTests", meansTests);
    } else {
      formik.setFieldValue("meansTests", []);
    }
  }, [selectAdminData.applicantDocumentState.applicantDocuments]);

  useEffect(() => {
    if (fullApplicationDetails) {
      formikMeans.setFieldValue(
        "threshhold",
        fullApplicationDetails.studyDetails.threshhold
      );
    }
  }, [fullApplicationDetails, formik]);

  const validationSchema = yup.object({});
  const formikMeans: FormikProps<MeansTestDto> = useFormik<MeansTestDto>({
    initialValues: {
      employementStatus: "",
      fullName: "",
      identityNumber: "",
      monthOne: 0,
      monthThree: 0,
      monthTwo: 0,
      threshhold:0
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values: MeansTestDto) => {
      setShowResults(true);
      let legalGuardians = formik.values.meansTests as MeansTestDto[];

      const index = legalGuardians.findIndex(
        (s) => s.identityNumber === values.identityNumber
      );

      if (index !== -1) {
        legalGuardians[index].monthOne = values.monthOne;
        legalGuardians[index].monthTwo = values.monthTwo;
        legalGuardians[index].monthThree = values.monthThree;
        legalGuardians[index].threshhold =  values.threshhold;
      }

      formik.setFieldValue("meansTests", legalGuardians);
      formikMeans.resetForm();
    },
  });

  const handleCheckboxChange = (event: any) => {
    const checked = event.target.checked;
    setIsEnabled(checked);
  };

  /** Avoid eligibility compare while threshold is still 0 (detail not loaded); honour admin override when enabled. */
  const comparisonMeansThreshold = isEnabled
    ? Number(formikMeans.values.threshhold) || 0
    : (fullApplicationDetails?.studyDetails?.threshhold ??
        Number(formikMeans.values.threshhold)) || 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {/* <Typography
          variant="body2"
          component="h2"
          sx={{ borderLeft: "3px solid #85B825", paddingLeft: 1, mb: 3 }}
        >
          Guardian Income Documents
        </Typography> */}
        <CircularLoader
          status={selectAdminData.applicantDocumentState.status}
        />

        {/* {selectAdminData.applicantDocumentState.status === "idle" && (
          <Grid container spacing={1} mb={3}>
            {data?.documents.map(
              (applicantDocuments: DocumentsViewDto, key: React.Key) => {
                return (
                  <Grid item xs={2.3} key={key}>
                    <ExtensionChecker document={applicantDocuments} />
                  </Grid>
                );
              }
            )}
          </Grid>
        )} */}
        <Grid item xs={12}>
          <Typography component="h5" variant="body2">
            Original threashhold: {Zar.format(fullApplicationDetails ? fullApplicationDetails?.studyDetails.threshhold: 0)}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <TextField
          size="medium"
          fullWidth
          id="threshhold"
          name="threshhold"
          label="Threashhold Amount"
          value={formikMeans.values.threshhold}
          onChange={formikMeans.handleChange}
          onBlur={formikMeans.handleBlur}
          type="number"
          disabled={!isEnabled}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="medium"
                      checked={isEnabled}
                      onChange={handleCheckboxChange}
                      color="primary"
                      sx={{
                        padding: "0 8px 0 0",
                        "& .MuiSvgIcon-root": { fontSize: 18 },
                      }}
                    />
                  }
                  label=""
                  sx={{
                    margin: 0,
                    "& .MuiFormControlLabel-label": { display: "none" },
                  }}
                />
              </InputAdornment>
            ),
          }}
          error={
            formikMeans.touched.threshhold &&
            Boolean(formikMeans.errors.threshhold)
          }
          helperText={
            formikMeans.touched.threshhold &&
            (formikMeans.errors.threshhold as ReactNode)
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              paddingLeft: "4px",
            },
            "& .MuiInputBase-input": {
              padding: "8.5px 14px",
            },
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <GuardianDropdown
          formik={formikMeans}
          guardians={formik.values.meansTests}
        />
      </Grid>

      <Grid item xs={3}>
        <TextField
          fullWidth
          size="small"
          id="monthOne"
          name="monthOne"
          label="Enter Income for first month"
          value={formikMeans.values.monthOne}
          onChange={formikMeans.handleChange}
          onBlur={formikMeans.handleBlur}
          type="number"
          error={
            formikMeans.touched.monthOne && Boolean(formikMeans.errors.monthOne)
          }
          helperText={
            formikMeans.touched.monthOne &&
            (formikMeans.errors.monthOne as ReactNode)
          }
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          size="small"
          fullWidth
          id="monthTwo"
          name="monthTwo"
          label="Enter Income for second month"
          value={formikMeans.values.monthTwo}
          onChange={formikMeans.handleChange}
          onBlur={formikMeans.handleBlur}
          type="number"
          error={
            formikMeans.touched.monthTwo && Boolean(formikMeans.errors.monthTwo)
          }
          helperText={
            formikMeans.touched.monthTwo &&
            (formikMeans.errors.monthTwo as ReactNode)
          }
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          size="small"
          fullWidth
          id="monthThree"
          name="monthThree"
          label="Enter Income for third month"
          value={formikMeans.values.monthThree}
          onChange={formikMeans.handleChange}
          onBlur={formikMeans.handleBlur}
          type="number"
          error={
            formikMeans.touched.monthThree && Boolean(formik.errors.monthThree)
          }
          helperText={
            formikMeans.touched.monthThree &&
            (formikMeans.errors.monthThree as ReactNode)
          }
        />
      </Grid>

      {formikMeans.values.identityNumber && (
        <Grid item xs={12}>
          <Button variant="outlined" onClick={() => formikMeans.handleSubmit()}>
            Add Income to {formikMeans.values.fullName}
          </Button>
        </Grid>
      )}

      <Grid item xs={12}>
        <Grid container spacing={2}>
          {selectAdminData.applicantDocumentState.status === "idle" &&
            formik.values.meansTests.map(
              (guardian: MeansTestDto, key: React.Key) => {
                return (
                  <Grid item xs={6} key={key}>
                    <Box className="meansTest_guardian_container">
                      <Box>
                        <Typography
                          variant="body2"
                          component="h2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {`${guardian.fullName}`}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" component="h2">
                          {guardian.identityNumber}
                        </Typography>
                        <Typography variant="body2" component="h2">
                          {guardian.employementStatus !== "-1"
                            ? guardian.employementStatus
                            : "Unknown"}
                        </Typography>
                      </Box>

                      <GuardianSalaryCalculation guardian={guardian} />
                    </Box>
                  </Grid>
                );
              }
            )}
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Box>
              <DisplayMoney
                amount={calculateTotalMonthlyIncome()}
                label="Total Monthly Household Income"
              />

              <DisplayMoney
                amount={calculateTotalDependantMonthlyIncome()}
                label={`Total Monthly Calculated value of dependants [${application.numberOfDependants}]`}
              />
            </Box>
            <Box>
              <DisplayMoney
                amount={calculateTotalYearlyIncome()}
                label="Total Annual Household Income"
              />
              <DisplayMoney
                amount={calculateTotalDependantYearlyIncome()}
                label={`Total Annual Calculated value of dependants [${application.numberOfDependants}]`}
              />
            </Box>
          </Grid>

          {showResults && comparisonMeansThreshold > 0 && (
            <Grid item xs={12}>
              {calculateTotalDependantYearlyIncome() >
              comparisonMeansThreshold ? (
                <Alert icon={<CloseIcon fontSize="inherit" />} severity="error">
                  The applicant is ineligible for a Thuthuka bursary, reason:
                  above threashhold
                </Alert>
              ) : (
                <Alert
                  icon={<CheckIcon fontSize="inherit" />}
                  severity="success"
                >
                  The applicant qualifies for thuthuka bursary
                </Alert>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
